import applyCaseMiddleware from 'axios-case-converter'
import axios from 'axios'
import axiosRetry from 'axios-retry'
import { newrelic } from 'lib/newrelic'

const createBaseAxiosInstance = () => {
  const csrfToken = document.querySelector<HTMLMetaElement>(
    'meta[name=csrf-token]'
  )?.content

  const axiosInstance = axios.create()
  axios.defaults.headers.common['X-Csrf-Token'] = csrfToken
  axiosInstance.defaults.headers.common['X-Csrf-Token'] = csrfToken

  // Apply retry logic
  axiosRetry(axiosInstance, {
    retryCondition: (e) => {
      return (
        (!e.response && !e.code) || [502, 503, 504, 520, 429].includes(e.response?.status)
      )
    },
    retryDelay: (retryCount, error) => {
      if (error.response) {
        const retry_after = error.response.headers['retryAfter'] || error.response.headers['retry-after']
        if (retry_after && !Number.isNaN(retry_after)) {
          return Number(retry_after) * 1000 // Convert seconds to milliseconds
        }
      }

      // Can also just return 0 here for no delay if one isn't specified
      return axiosRetry.exponentialDelay(retryCount)
    },
  })

  return axiosInstance
}

const baseAxiosInstance = createBaseAxiosInstance()

const restClientWithCaseConversion = applyCaseMiddleware(baseAxiosInstance)

const restClientWithoutCaseConversion = createBaseAxiosInstance()

const addStackTraceCapturing = (client) => {
  // Wrap get, post, put, delete in custom function to capture stack trace
  const originalGet = client.get
  client.get = function (url, config) {
    const stackTrace = new Error('Capture stack trace').stack

    const modifiedConfig = config || {}
    modifiedConfig.metadata = modifiedConfig.metadata || {}
    modifiedConfig.metadata.initiatingStackTrace = stackTrace

    const args = [url, modifiedConfig]

    return originalGet.apply(this, args)
  }

  const originalPost = client.post
  client.post = function (url, data, config) {
    const stackTrace = new Error('Capture stack trace').stack

    const modifiedConfig = config || {}
    modifiedConfig.metadata = modifiedConfig.metadata || {}
    modifiedConfig.metadata.initiatingStackTrace = stackTrace

    const args = [url, data, modifiedConfig]

    return originalPost.apply(this, args)
  }

  const originalPut = client.put
  client.put = function (url, data, config) {
    const stackTrace = new Error('Capture stack trace').stack

    const modifiedConfig = config || {}
    modifiedConfig.metadata = modifiedConfig.metadata || {}
    modifiedConfig.metadata.initiatingStackTrace = stackTrace

    const args = [url, data, modifiedConfig]

    return originalPut.apply(this, args)
  }

  const originalDelete = client.delete
  client.delete = function (url, config) {
    const stackTrace = new Error('Capture stack trace').stack

    const modifiedConfig = config || {}
    modifiedConfig.metadata = modifiedConfig.metadata || {}
    modifiedConfig.metadata.initiatingStackTrace = stackTrace

    const args = [url, modifiedConfig]

    return originalDelete.apply(this, args)
  }

  return client
}

// JS errors on public pages shouldn't redirect to login
function onPublicPath() {
  const currentPath = window.location.pathname
  if (currentPath.startsWith('/global_admin')){
    return false
  }

  const adminUrl = window.ENV.CLIENT_URL
  const adminHost = new URL(adminUrl).host
  const currentHost = window.location.host

  // Check if the URL is on the same host as the admin URL

  return adminHost !== currentHost || currentPath.startsWith('/p/') || currentPath.startsWith('/target/')
}

const addErrorInterceptors = (client) => {
  client.interceptors.response.use(undefined, async (error) => {
    if (error.response && !onPublicPath()) {
      // network error etc. don't have response
      if (
        (error.response.status === 403 || error.response.status === 422) &&
        !error.config.url.includes('/api/users/logged_in') // stop the infinite loop of 403 errors
        ) {
        const returnTo = encodeURIComponent(window.location.pathname + window.location.search)
        const signInUrl = `/users/sign_in?client_side_logout=1&redirect=${returnTo}`

        try {
          const loginStatus = await client.get('/api/users/logged_in')
          if (loginStatus.data.loggedIn === false) {
            // If the user is logged out, redirect to the login page
            window.location.href = signInUrl
            return new Promise(() => {}) // Prevent the rest of the chain from executing
          }
        } catch (e) {
          if (e.response?.status === 403) {
            window.location.href = signInUrl
            return new Promise(() => {}) // Prevent the rest of the chain from executing
          }
        }
      }
    }

    return Promise.reject(error)
  })

  client.interceptors.response.use(undefined, (error) => {
    const config = error?.config || {}
    const response = error?.response || {}

    error.name = `${(config.method || '').toUpperCase()} ${config.url} ${response.status}`
      .replace(/\/\d+/g, '/[id]')
      .replace(/[a-f0-9]{32}/g, '[hash]')

    if (config.metadata?.initiatingStackTrace) {
      // Assuming error.stack and config.metadata.initiatingStackTrace are both strings
      const originalStackArray = error.stack.split('\n')
      const initiatingStackArray = config.metadata.initiatingStackTrace.split('\n')

      // Concatenate the arrays correctly using the spread operator or concat method
      const totalStack = [...originalStackArray, ...initiatingStackArray]
        .filter((line) => !line.includes('nrWrapper')) // Filter out 'nrWrapper' lines
        .filter((line) => !line.includes('Error: Capture stack trace')) // Filter out the custom error line

      error.stack = totalStack.join('\n')
    }

    newrelic?.noticeError(error)

    return Promise.reject(error)
  })

  return client
}

const withCaseConversion = addErrorInterceptors(addStackTraceCapturing(restClientWithCaseConversion))
const withoutCaseConversion = addErrorInterceptors(addStackTraceCapturing(restClientWithoutCaseConversion))

export default withCaseConversion

export const restClient = withCaseConversion
export const restClientRaw = withoutCaseConversion
